import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, Table, Loading, SearchInput, Pagination, ConfirmDialog, Badge } from '../../components/Common';
import api from '../../services/api';

const AdvertisementList = () => {
  const navigate = useNavigate();
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, size: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, ad: null });
  const [filterType, setFilterType] = useState('');
  const [filterSlot, setFilterSlot] = useState('');
  const [adTypes, setAdTypes] = useState([]);
  const [adSlots, setAdSlots] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/advertisements/config/options');
        const data = response.data.data;
        setAdTypes([
          { value: '', label: 'All Types' },
          ...data.types.map(t => ({ value: String(t.id), label: t.name }))
        ]);
        setAdSlots([
          { value: '', label: 'All Slots' },
          ...data.slots.map(s => ({ value: s.key, label: s.label }))
        ]);
      } catch (error) {
        setAdTypes([
          { value: '', label: 'All Types' },
          { value: '1', label: 'Banner' },
          { value: '2', label: 'Sidebar' },
          { value: '3', label: 'In-Article' },
          { value: '4', label: 'Popup' },
          { value: '5', label: 'Native' },
          { value: '6', label: 'Video' },
          { value: '7', label: 'Interstitial' },
        ]);
        setAdSlots([
          { value: '', label: 'All Slots' },
          { value: 'home-top-banner', label: 'Home Top Banner' },
          { value: 'home-mid-banner', label: 'Home Mid Banner' },
          { value: 'sidebar-top', label: 'Sidebar Top' },
          { value: 'sidebar-bottom', label: 'Sidebar Bottom' },
          { value: 'article-mid', label: 'Article Mid' },
          { value: 'article-bottom', label: 'Article Bottom' },
          { value: 'sidebanner', label: 'Sidebanner' },
          { value: 'footer-banner', label: 'Footer Banner' },
        ]);
      }
    };
    fetchConfig();
  }, []);

  const fetchAdvertisements = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
      };
      if (filterType) params.type_id = filterType;
      if (filterSlot) params.slot = filterSlot;

      const response = await api.get('/advertisements', { params });
      setAdvertisements(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
      }));
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, filterType, filterSlot]);

  useEffect(() => {
    fetchAdvertisements();
  }, [fetchAdvertisements]);

  const handleDelete = async () => {
    if (!deleteModal.ad) return;
    try {
      await api.delete(`/advertisements/${deleteModal.ad.id}`);
      fetchAdvertisements();
      setDeleteModal({ open: false, ad: null });
    } catch (error) {
      console.error('Error deleting advertisement:', error);
    }
  };

  const handleToggleStatus = async (ad) => {
    try {
      await api.put(`/advertisements/${ad.id}`, { isActive: !ad.is_active });
      fetchAdvertisements();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{row.name}</p>
          <p className="text-sm text-gray-500 capitalize">{row.slot?.replace(/-/g, ' ')}</p>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (value, row) => {
        if (!row) return null;
        const tp = row.advertisementType;
        return (
          <div>
            <Badge variant="info">{tp?.name || row.type_id || 'N/A'}</Badge>
            {tp?.description && (
              <p className="text-xs text-gray-400 mt-0.5">{tp.description}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'format',
      label: 'Format',
      render: (value, row) => {
        if (!row) return null;
        const fmt = row.advertisementFormat;
        const label = fmt
          ? `${fmt.name}${fmt.width && fmt.height ? ` (${fmt.width}\u00D7${fmt.height})` : ''}`
          : (row.format_id || 'auto');
        return (
          <span className="text-sm text-gray-600">{label}</span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => (
        <Badge variant={row.is_active ? 'success' : 'danger'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'stats',
      label: 'Performance',
      render: (value, row) => (
        <div className="text-sm">
          <p className="text-gray-600">{row.impressions || 0} impressions</p>
          <p className="text-gray-600">{row.clicks || 0} clicks</p>
          <p className="text-blue-600 font-medium">
            {row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : 0}% CTR
          </p>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleToggleStatus(row)}
            className={`p-1.5 rounded-lg transition-colors ${
              row.is_active ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'
            }`}
            title={row.is_active ? 'Pause' : 'Activate'}
          >
            {row.is_active ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          </button>
          <button
            onClick={() => navigate(`/advertisements/edit/${row.id}`)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/advertisements/stats/${row.id}`)}
            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="View Stats"
          >
            <ChartBarIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteModal({ open: true, ad: row })}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (loading && advertisements.length === 0) {
    return <Loading fullScreen text="Loading advertisements..." />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Advertisements</h1>
          <p className="text-slate-500">Manage ad placements and campaigns</p>
        </div>
        <Button onClick={() => navigate('/advertisements/create')} className="flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Create Advertisement
        </Button>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search advertisements..."
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {adTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={filterSlot}
            onChange={(e) => setFilterSlot(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {adSlots.map(slot => (
              <option key={slot.value} value={slot.value}>{slot.label}</option>
            ))}
          </select>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-500">Total Ads</p>
          <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Active Ads</p>
          <p className="text-2xl font-bold text-green-600">
            {advertisements.filter(a => a.is_active).length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Total Impressions</p>
          <p className="text-2xl font-bold text-blue-600">
            {advertisements.reduce((sum, a) => sum + (a.impressions || 0), 0).toLocaleString()}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Total Clicks</p>
          <p className="text-2xl font-bold text-purple-600">
            {advertisements.reduce((sum, a) => sum + (a.clicks || 0), 0).toLocaleString()}
          </p>
        </Card>
      </div>

      <Card>
        <Table
          columns={columns}
          data={advertisements}
          loading={loading}
          emptyMessage="No advertisements found"
        />
        {pagination.total > pagination.size && (
          <div className="mt-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={Math.ceil(pagination.total / pagination.size)}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, ad: null })}
        onConfirm={handleDelete}
        title="Delete Advertisement"
        message={`Are you sure you want to deactivate "${deleteModal.ad?.name}"? The ad will be hidden from public view but can be restored later.`}
        confirmText="Deactivate"
        confirmVariant="danger"
      />
    </div>
  );
};

export default AdvertisementList;
